import { SkillsTutorProduct } from '@features/skillsTutor/types'

// Skills Tutor level constants
const SKILL_LEVEL_CONFIG = {
  BASIC: [3] as readonly number[],
  INTERMEDIATE: [5] as readonly number[],
}

export const getSkillsTutorLevel = (levelRange?: string | null): number | null => {
  if (!levelRange) {
    return null
  }

  const firstPart = levelRange.split(';')[0]
  const parsed = Number(firstPart)

  if (Number.isNaN(parsed)) {
    return null
  }

  return parsed
}

export const getSkillsTutorLevelLabelFromLevel = (level?: number | null): string | undefined => {
  if (level == null) {
    return undefined
  }

  if (SKILL_LEVEL_CONFIG.BASIC.includes(level)) {
    return 'Basic'
  }

  if (SKILL_LEVEL_CONFIG.INTERMEDIATE.includes(level)) {
    return 'Intermediate'
  }

  return undefined
}

export const getSkillsTutorLevelLabelFromRange = (levelRange?: string | null): string | null => {
  const level = getSkillsTutorLevel(levelRange)

  if (level == null) {
    return null
  }

  const label = getSkillsTutorLevelLabelFromLevel(level)

  return label ?? null
}

export interface SkillsTutorWithLevel {
  ProductInfo: SkillsTutorProduct['ProductInfo']
  level: string
}

export interface SkillsTutorAssignmentsDiff {
  toAssign: string[]
  toUnassign: string[]
}

export const getBasicOrInterSkillsTutors = (skillsTutors: SkillsTutorProduct[]): SkillsTutorWithLevel[] => {
  const validLevels = [...SKILL_LEVEL_CONFIG.BASIC, ...SKILL_LEVEL_CONFIG.INTERMEDIATE]
  
  return (skillsTutors || [])
    .map((st) => {
      const numericLevel = getSkillsTutorLevel(st?.ProductInfo?.LevelRange)
      return {
        ...st,
        level: numericLevel != null ? String(numericLevel) : '',
      }
    })
    .filter((st) => {
      const numericLevel = st.level ? Number(st.level) : undefined
      return numericLevel !== undefined && validLevels.includes(numericLevel)
    })
}

export const getSkillsTutorLabel = (name: string | undefined, level: string): string => {
  const numericLevel = level ? Number(level) : undefined
  const levelLabel = getSkillsTutorLevelLabelFromLevel(numericLevel)

  if (!name) {
    return levelLabel ? `${levelLabel}` : ''
  }

  const sanitizedName = name.replace(/\s[0-9]{1,2}$/, '').trim()

  return levelLabel ? `${sanitizedName} (${levelLabel})` : sanitizedName
}

export const diffSkillsTutorAssignments = (
  selectedIds: string[],
  assignedMaterials: { MaterialId?: string | null }[] | undefined
): SkillsTutorAssignmentsDiff => {
  const assignedIds =
    assignedMaterials
      ?.map((material) => material.MaterialId)
      .filter((id): id is string => Boolean(id)) || []

  const toUnassign = assignedIds.filter((id) => !selectedIds.includes(id))
  const toAssign = selectedIds.filter((id) => !assignedIds.includes(id))

  return {
    toAssign,
    toUnassign,
  }
}
